/* eslint-disable unicorn/consistent-function-scoping */
// Each slice have your own state.
import { useSelector } from 'react-redux';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
import { AppState } from 'app/App.store';
import { Dispatch } from 'redux';
import axios from 'axios';
import axiosClient from '@ui-core/config/axiosClient';
import {
    ErrorType,
    isAuthenticated
} from '../../../../app/utils/authentication';
import { setExpirationTokenDate } from '@ui-core/utils/setExpirationTokenDate.lib';
import { setExpirationAccessTokenDate } from '@ui-core/utils/setExpirationAccessTokenDate.lib';
import api from '@ui-core/components/SSONavItem/services/TokenAV1';
import { TokenAV1I } from '@ui-core/components/SSONavItem/interfaces/TokenAV1Interface';
import { RenewJWTThunkBodyType } from '@ui-core/Types/RenewJWTThunkBodyType';
import { getSessionIsTaken } from '@ui-core/utils/getSessionIsTaken.lib';

export type AuthDataType = {
    accessToken: string;
};

export interface SessionState {
    loading: boolean;
    isAuthenticated: boolean;
    authData: AuthDataType;
    error: ErrorType;
    errorMessage: string;
    updatedAt: number;
    intentos: number;
    is429Error: boolean;
}

/**
 * In the initial state, we defined all the component or container (or view,
 * whatever) states. It's very familiar to useState.
 */
const initialState: SessionState = {
    loading: false,
    isAuthenticated: isAuthenticated(),
    authData: {} as AuthDataType,
    error: ErrorType.NOT_ERROR,
    errorMessage: '',
    updatedAt: moment().unix(),
    intentos: -1,
    is429Error: false
};

export const sessionSlice = createSlice({
    name: 'session',
    initialState,
    reducers: {
        loading: (state) => {
            // Redux Toolkit allows us to write "mutating" logic in reducers. It
            // doesn't actually mutate the state because it uses the Immer library,
            // which detects changes to a "draft state" and produces a brand new
            // immutable state based off those changes
            state.loading = true;
            state.error = ErrorType.NOT_ERROR;
            state.errorMessage = '';
        },
        loginSuccess: (state, { payload }: PayloadAction<AuthDataType>) => {
            state.loading = false;
            state.isAuthenticated = true;
            state.authData = payload;
            state.updatedAt = moment().unix();
            state.is429Error = false;
        },
        logout: (state) => {
            state.isAuthenticated = false;
            state.authData = {} as AuthDataType;
            state.updatedAt = moment().unix();
        },
        loginError: (state, { payload }: PayloadAction<string>) => {
            state.loading = false;
            state.error = JSON.parse(payload).error;
            state.intentos =
                typeof JSON.parse(payload).intentos === 'number'
                    ? JSON.parse(payload).intentos
                    : -1;
            state.errorMessage = JSON.parse(payload).message;
            state.is429Error = false;
        },
        clearLoginError: (state) => {
            state.error = ErrorType.NOT_ERROR;
            state.errorMessage = '';
            state.intentos = -1;
            state.is429Error = false;
        },
        set429Error: (state) => {
            state.is429Error = true;
        },
        setInitialAuthenticatedState: (state) => {
            state.isAuthenticated = true;
        },
        stopLoading: (state) => {
            state.loading = false;
        }
    }
});

// Action creators are generated for each case reducer function
export const {
    loading,
    loginSuccess,
    logout,
    loginError,
    clearLoginError,
    set429Error,
    setInitialAuthenticatedState,
    stopLoading
} = sessionSlice.actions;

export const useSessionSelector = () =>
    useSelector<AppState, SessionState>(
        ({ [sessionSlice.name]: slice }) => slice
    );

export type UserType = {
    id: string;
    contrasenia: string;
};

export const userIsAuthenticated =
    () =>
    (dispatch: Dispatch): void => {
        dispatch(setInitialAuthenticatedState());
    };

const renovarSessionAv1 = async () => {
    const rToken = localStorage.getItem('RefreshToken');
    const response = await api.refreshTokenAV1(rToken);
    const { access_token, refresh_token }: TokenAV1I = response.data;
    localStorage.setItem('AccessToken', access_token);
    localStorage.setItem('RefreshToken', refresh_token);
    setExpirationAccessTokenDate(`Bearer ${access_token}`);
};

export const renewJWTThunk =
    (body: RenewJWTThunkBodyType) =>
    async (dispatch: Dispatch): Promise<void> => {
        dispatch(loading());
        const { renewOriginalSession } = body;
        try {
            const apiAxios = axiosClient(
                `${import.meta.env.REACT_APP_LOGIN_API_URL}`
            );
            const response = await apiAxios.post('v1/sesion/renovar', body);

            if (!renewOriginalSession) {
                localStorage.setItem(
                    'Authorization',
                    `Bearer ${response.data.accessToken}`
                );
                setExpirationTokenDate(`Bearer ${response.data.accessToken}`);
                await renovarSessionAv1();
                dispatch(loginSuccess(response.data));
            } else {
                const sessionIsTaken = getSessionIsTaken();

                // Renew AV2 Token.
                sessionIsTaken.Authorization = `Bearer ${response.data.accessToken}`;
                sessionIsTaken.expirationTokenDate =
                    setExpirationTokenDate(
                        `Bearer ${response.data.accessToken}`,
                        true
                    ) ?? '';

                // Renew AV1 Token.
                const rToken = sessionIsTaken.RefreshToken;
                const rTokenResponse = await api.refreshTokenAV1(rToken);
                const { access_token, refresh_token }: TokenAV1I =
                    rTokenResponse.data;
                sessionIsTaken.AccessToken = access_token;
                sessionIsTaken.RefreshToken = refresh_token;
                sessionIsTaken.expirationAccessTokenDate =
                    setExpirationAccessTokenDate(
                        `Bearer ${access_token}`,
                        true
                    ) ?? '';

                // Store original session updated in stringify.
                localStorage.setItem(
                    'sessionIsTaken',
                    JSON.stringify(sessionIsTaken)
                );
            }
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                // eslint-disable-next-line no-console
                console.log('Renew JWT error:', error.response);
                if (error.response && error.response.status === 429)
                    dispatch(set429Error());
                dispatch(loginError('Error'));
            }
        }
    };

export const requestLogoutUser = async (isTempSession = false) => {
    try {
        const apiAxios = axiosClient(
            `${import.meta.env.REACT_APP_LOGIN_API_URL}`
        );
        const accessToken = `${localStorage.getItem('AccessToken')}`;
        await apiAxios.post(
            isTempSession
                ? 'v1/sesion/cerrar/sessionTemporal'
                : 'v1/sesion/cerrar',
            {},
            {
                headers: {
                    AccessToken: accessToken
                }
            }
        );
    } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.log(error);
    }
};
